<template>
	<div v-if="showCustomerDetails" class="space-y-4 my-4">
		<div v-if="!loggingIn">
			<div class="grid grid-cols-1 sm:grid-cols-1 w-full gap-y-4 sm:gap-x-2">
				<div class="flex relative w-full justify-start sm:col-span-1">
					<input
						type="email"
						required
						v-model="email"
						id="guest-email"
						placeholder=" "
						class="text-ihh-midnight-gray order-1 sm:order-none col-span-1 focus:border-ihh-dark-salmon focus:outline-0 focus:shadow shadow-ihh-dark-salmon border border-gray-200 rounded px-2.5 pt-4 pb-1.5 flex w-full text-sm bg-gradient-to-r from-ihh-pink/40 to-ihh-light-lavendar/40 sm:col-span-1 peer"
					/>
					<label
						for="guest-email"
						class="absolute left-2.5 text-ihh-midnight-gray transition-all duration-200 transform -translate-y-1/2 pointer-events-none peer-placeholder-shown:top-1/2 peer-placeholder-shown:text-base peer-focus:top-3 peer-focus:text-xs peer-focus:text-ihh-midnight-gray top-3 text-xs"
					>
						Email
					</label>
				</div>
				<button
					@click="setGuest"
					class="order-3 sm:order-none col-span-1 uppercase bg-ihh-dark-salmon hover:bg-ihh-salmon ease-in-out duration-100 transition text-ihh-midnight-gray font-semibold tracking-wide flex w-full min-w-40 justify-center text-center py-3 px-4 text-sm rounded items-center"
				>
					Continue
				</button>
				<div class="order-2 sm:order-none col-span-2 flex items-center m-auto w-full pr-4">
					<label class="custom-checkbox relative inline-block overflow-hidden w-4 h-4 bg-transparent rounded border border-gray-300 cursor-pointer mr-2">
						<input v-model="subscribe" type="checkbox" class="absolute opacity-0 w-0 h-0" />
						<span class="checkmark absolute inset-0 flex items-center justify-center hidden text-ihh-white/90 text-lg"> ✓ </span>
					</label>
					<div class="order-4 sm:order-none col-span-2 text-ihh-midnight-gray text-sm items-center text-start">Subscribe to our newsletter.</div>
				</div>
			</div>
			<div class="text-ihh-midnight-gray text-sm text-start pt-4">
				Already have an account?
				<button @click="loggingIn = true" class="text-ihh-dark-salmon underline underline-offset-2">Sign in now</button>
			</div>
		</div>

		<div v-if="loggingIn" class="w-full">
			<Login v-slot="{ login }" :small="false" class="!p-0 !m-0" @loggedIn="">
				<div class="flex flex-col w-full items-center">
					<div class="w-full my-4">
						<input
							v-model="email"
							type="text"
							placeholder="Email"
							required
							class="py-3 pl-4 border border-gray-400 !focus:border-ihh-salmon !focus:bg-white rounded w-full text-sm md:text-base bg-ihh-input-background text-ihh-input-text font-light placeholder-ihh-input-text"
						/>
					</div>

					<div class="w-full mt-4 mb-2">
						<input
							v-model="password"
							type="password"
							placeholder="Password"
							required
							class="p-3 pl-4 border border-gray-400 !focus:border-ihh-salmon !focus:bg-white rounded w-full text-sm md:text-base bg-ihh-input-background text-ihh-input-text font-light placeholder-ihh-input-text"
						/>
					</div>

					<div class="w-full flex justify-between text-ihh-dark-salmon text-xs">
						<a href="/login?resetPassword=true">Reset Password</a>
						<a href="/login?createAccount=true">Create an account</a>
					</div>

					<div class="w-full flex justify-start space-x-3 mt-6">
						<button
							@click="login(email, password)"
							class="flex text-center items-center justify-center text-sm md:text-sm text-ihh-midnight-gray py-2 px-10 rounded-sm bg-ihh-dark-salmon hover:bg-ihh-salmon duration-150 transition tracking-wide font-semibold"
						>
							Login
						</button>
						<button @click="loggingIn = false" class="border border-gray-300 text-sm rounded-sm px-10 text-ihh-midnight-gray">Cancel</button>
					</div>
				</div>
			</Login>
		</div>
	</div>
</template>

<script>
import Login from '../layout/login.vue'
import axios from 'axios'
import Swal from 'sweetalert2'
import helpers from '../../helpers/checkout.js'
import { validateEmail } from '../../helpers/email.js'
import { useCheckoutStore } from '../../stores/store.js'

export default {
	name: 'CheckoutLogin',
	components: { Login },
	emits: ['customer-continue'],
	inject: ['store'],
	props: {
		showCustomerDetails: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			email: '',
			password: '',
			subscribe: false,
			loggingIn: false,
			checkout: useCheckoutStore(),
		}
	},
	mounted() {
		const stored = helpers.getCustomerEmail()
		if (stored && validateEmail(stored)) {
			this.email = stored
		} else if (stored) {
			this.checkout.updateGuestEmail('')
		}
		// Handle browser autofill which may not trigger v-model updates
		this.checkAutofill()
	},
	methods: {
		checkAutofill() {
			// Browser autofill doesn't always trigger input events
			// Check after a short delay to capture autofilled values
			setTimeout(() => {
				const emailInput = document.getElementById('guest-email')
				if (emailInput?.value && validateEmail(emailInput.value)) {
					this.email = emailInput.value
				}
			}, 100)
		},
		setGuest() {
			const emailInput = document.getElementById('guest-email')
			if (emailInput?.value && validateEmail(emailInput.value)) {
				this.email = emailInput.value
			}

			const validated = this.validateGuestEmail()
			if (!validated) return

			if (this.subscribe) {
				this.guestSubscribe()
			}

			this.$emit('customer-continue', this.email)
		},
		guestSubscribe() {
			axios.post(`${this.store.api}/controllers/sendlane/newsletter`, { email: this.email })
		},
		validateGuestEmail() {
			const trimmed = (this.email || '').trim()
			if (validateEmail(trimmed)) {
				this.email = trimmed
				this.checkout.updateGuestEmail(trimmed)
				return true
			}
			this.checkout.updateGuestEmail('')
			Swal.fire({
				icon: 'error',
				title: 'Invalid Email',
				text: 'Please enter a valid email address (for example name@example.com) to continue.',
				confirmButtonText: 'OK',
			})
			return false
		},
	},
}
</script>

<style scoped>
.peer:focus::placeholder {
	color: transparent;
}
.peer::placeholder {
	color: transparent;
}
</style>
